import { Typography, Flex, Spin, Tag, Popover } from 'antd'
import { CSSProperties, useState } from 'react'
import { FileTextOutlined } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons'
import { useDraggable } from '@dnd-kit/core'
import { useGetSchedulingJobsQuery } from '../scheduleQueries'
import CreateEventFromJobModal from './CreateEventFromJobModal'
import { SchedulingJob } from '../scheduleApi'
import { ScheduleCreateEventFilters, filterJobs } from './scheduleCreateEventModel'
import { ProjectStatuses, ProjectStatus } from '../../../common/constants'
import { Dayjs } from 'dayjs'

const isUnscheduledStatus = (status: ProjectStatus | null | undefined) => status == null || status === ProjectStatuses.Unscheduled

const { Text } = Typography

type GroupedJobs = {
  [customerId: number]: {
    customerDisplayName: string
    jobs: SchedulingJob[]
  }
}

const clientHeaderStyle: CSSProperties = {
  padding: '12px',
  backgroundColor: '#f5f5f5',
  fontWeight: 600,
  color: '#1e1e1e',
  fontSize: '14px',
  marginTop: '0px'
}


const jobRowStyle: CSSProperties = {
  padding: '12px',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  borderBottom: '1px solid #f0f0f0'
}

type DraggableJobRowProps = {
  isNested?: boolean
  job: SchedulingJob
  isDisabled: boolean
  onClick: () => void
}

const DraggableJobRow = ({ job, isDisabled, onClick, isNested = false }: DraggableJobRowProps) => {
  const [expanded, setExpanded] = useState(false)
  const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
    id: `job-${job.jobId}`,
    data: { type: 'job', job },
    disabled: isDisabled,
  })

  const jobAddressParts = [
    job.address1,
    job.address2,
    job.city,
    job.state,
    job.zip
  ].filter(Boolean)
  const jobAddress = jobAddressParts.join(', ')

  const style: CSSProperties = {
    ...jobRowStyle,
    cursor: isDisabled ? 'not-allowed' : 'grab',
    opacity: isDragging ? 0.5 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    padding: 12
  }

  const hasWorkAreas = job.approvedWorkAreas.length > 0

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  return (
    <div>
      <div
        ref={setNodeRef}
        className={isDisabled ? 'job-row-disabled' : 'job-row-hover'}
        style={style}
        onClick={() => !isDisabled && !isDragging && onClick()}
        {...listeners}
        {...attributes}
      >
        <Flex gap={8} align="start">
          <div style={{ flex: 1 }}>
            <Flex justify="space-between" align="center" style={{ opacity: isDisabled ? 0.5 : 1 }}>
              <Flex gap={6} align="center">
                <Text strong>{job.jobName}</Text>
                {job.schedulingNotes && (
                  <Popover content={job.schedulingNotes} trigger="hover" styles={{ root: { maxWidth: 224 } }}>
                    <FileTextOutlined
                      style={{ fontSize: '14px', color: '#1677ff', cursor: 'pointer' }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popover>
                )}
              </Flex>
              {job.jobNumber && (
                <Flex gap={8} align="center" style={{ flexShrink: 0 }}>
                  <Text type="secondary" style={{ fontSize: '14px' }}>{job.jobNumber}</Text>
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    style={{ color: '#1677ff', cursor: 'pointer', fontSize: '12px' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(`/#!/job-redirect/${job.jobId}`, '_blank')
                    }}
                  />
                </Flex>
              )}
            </Flex>
            {!isNested &&
              <div style={{ opacity: isDisabled ? 0.5 : 1 }}>
                {job.customerDisplayName}
              </div>
            }
            <div style={{ opacity: isDisabled ? 0.5 : 1 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {jobAddress}
              </Text>
            </div>
            {isDisabled && (
              <div style={{ marginTop: '4px' }}>
                <Tag style={{ fontSize: '11px', color: '#000' }}>No Approved Work Areas</Tag>
              </div>
            )}
            {hasWorkAreas && (
              <div
                onClick={handleToggleExpand}
                style={{ marginTop: '4px' }}
              >
                <Text style={{ fontSize: '12px', color: '#1677ff', cursor: 'pointer' }}>
                  {expanded ? 'Hide Work Areas' : 'Show Work Areas'}
                </Text>
              </div>
            )}
          </div>
          {!job.jobNumber && (
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              style={{ color: '#1677ff', cursor: 'pointer', padding: '6px 0' }}
              onClick={(e) => {
                e.stopPropagation()
                window.open(`/#!/job-redirect/${job.jobId}`, '_blank')
              }}
            />
          )}
        </Flex>
      </div>
      {expanded && hasWorkAreas && (
        <div style={{ padding: '4px 12px 12px 24px', backgroundColor: '#fafafa' }}>
          {job.approvedWorkAreas.map((wa) => (
            <Flex
              key={wa.name}
              justify="space-between"
              align="center"
              style={{ padding: '8px 0', gap: 8 }}
            >
              <Text>{wa.name}</Text>
              {wa.status === ProjectStatuses.Completed ? (
                <Tag color="green" bordered={true} style={{ fontSize: '11px', margin: 0 }}>Complete</Tag>
              ) : wa.calendarEventCount === 0 ? (
                <Tag color="error" bordered={true} style={{ fontSize: '11px', margin: 0 }}>Unscheduled</Tag>
              ) : (
                <Tag bordered={true} style={{ fontSize: '11px', margin: 0 }}>Scheduled</Tag>
              )}
            </Flex>
          ))}
        </div>
      )}
    </div>
  )
}

type JobsListProps = {
  filterUnscheduled?: boolean
  filters: ScheduleCreateEventFilters
  scheduledJobs?: SchedulingJob[]
  isLoading?: boolean
  onEventCreated?: () => void
  selectedDate?: Dayjs
  onJobSelected?: (job: SchedulingJob) => void
}

const JobsList = ({ filterUnscheduled = false, filters, scheduledJobs, isLoading: isLoadingProp, onEventCreated, selectedDate, onJobSelected }: JobsListProps) => {
  const { data, isLoading: isLoadingQuery } = useGetSchedulingJobsQuery()

  const jobsData = scheduledJobs !== undefined ? scheduledJobs : data
  const isLoading = isLoadingProp !== undefined ? isLoadingProp : isLoadingQuery
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedJob, setSelectedJob] = useState<SchedulingJob | null>(null)

  if (isLoading) {
    return <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 12 }}><Spin /></div>
  }

  let filteredJobs = filterJobs(jobsData || [], filters)

  if (filterUnscheduled) {
    filteredJobs = filteredJobs.filter(job => isUnscheduledStatus(job.status) && job.approvedWorkAreas.some(wa => wa.calendarEventCount === 0 && isUnscheduledStatus(wa.status)))
    filteredJobs.sort((a, b) => {
      if (!a.soldDateUtc && !b.soldDateUtc) {
        return 0
      }
      if (!a.soldDateUtc) {
        return 1
      }
      if (!b.soldDateUtc) {
        return -1
      }
      return new Date(b.soldDateUtc).getTime() - new Date(a.soldDateUtc).getTime()
    })
  }

  const groupedJobs: GroupedJobs = filterUnscheduled ? {} : filteredJobs.reduce((acc, job) => {
    if (!acc[job.customerId]) {
      acc[job.customerId] = {
        customerDisplayName: job.customerDisplayName,
        jobs: []
      }
    }
    acc[job.customerId].jobs.push(job)
    return acc
  }, {} as GroupedJobs)

  const handleJobClick = (job: SchedulingJob) => {
    if (onJobSelected) {
      onJobSelected(job)
      return
    }
    setSelectedJob(job)
    setIsModalOpen(true)
  }

  if (filterUnscheduled ? filteredJobs.length === 0 : Object.keys(groupedJobs).length === 0) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#999' }}>
        No jobs available
      </div>
    )
  }

  return (
    <>
      <div>
        {filterUnscheduled ? (
          filteredJobs.map((job) => {
            const isDisabled = !job.hasApprovedWorkareas

            return (
              <div key={job.jobId}>
                <DraggableJobRow
                  job={job}
                  isDisabled={isDisabled}
                  onClick={() => handleJobClick(job)}
                />
              </div>
            )
          })
        ) : (
          Object.entries(groupedJobs).sort(([, a], [, b]) => a.customerDisplayName.localeCompare(b.customerDisplayName)).map(([customerId, clientData]) => (
            <div key={customerId}>
              <div style={clientHeaderStyle}>
                <Text strong>{clientData.customerDisplayName}</Text>
              </div>
              {[...clientData.jobs].sort((a, b) => a.jobName.localeCompare(b.jobName)).map((job) => {
                const isDisabled = !job.hasApprovedWorkareas

                return (
                  <div key={job.jobId}>
                    <DraggableJobRow
                      job={job}
                      isDisabled={isDisabled}
                      onClick={() => handleJobClick(job)}
                      isNested
                    />
                  </div>
                )
              })}
            </div>
          ))
        )}
      </div>

      {!onJobSelected && (
        <CreateEventFromJobModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedJob(null)
          }}
          job={selectedJob}
          onSuccess={onEventCreated}
          selectedDate={selectedDate}
        />
      )}

      <style>
        {`
          .job-row-hover:hover {
            background-color: #f8f8f8;
          }
        `}
      </style>
    </>
  )
}

export default JobsList
